import { useEffect, useMemo, useRef } from 'react';
import axiosInstance from '../fetchConfig/axiosConfig';
import { processCompRow } from './useStreamProcessorNew';
import { usePersistedDataStore } from '../components/store';
import logger from '../utils/logger';
import { idKey, normalizeCompId, findCompIndex } from '../components/CaseReview/common/compSheetLookup';

export function clearCompLookupCache() {
  clearQueue();
  shared.cache.clear();
  usePersistedDataStore.getState().incrementCompLookupCacheInvalidation?.();
}

const DISABLE_COMP_LOOKUPS = false;
const MAX_RETRIES = 3;
const BATCH_SIZE = 15;
const BATCH_DEBOUNCE_MS = 120; // wait this long after last enqueue before flushing

function lookupCacheKey({ pid, transactionId }) {
  if (pid != null && pid !== '') return `p:${pid}`;
  if (transactionId != null && transactionId !== '') return `t:${transactionId}`;
  return null;
}

function compLookupTaskKey(task) {
  return `${task.taxYear}::${lookupCacheKey(task)}::${task.target.caseIndex}`;
}

/**
 * Single shared engine: every `useCompLookups()` caller shares one queue, dedupe
 * sets, and cache. Otherwise the stream worker and e.g. IndividualAnalysisContainer
 * each had separate refs and the same PID could be QUEUEING twice.
 */
const shared = {
  cache: new Map(),
  pending: new Map(),
  queue: [],
  queuedKeys: new Set(),
  inFlightKeys: new Set(),
  debounceTimer: null,
  draining: false,
  maxConcurrent: 8,
};

function expectedSubjectPidForCase(negotiationObj, caseIndex) {
  if (typeof caseIndex !== 'number' || caseIndex < 0) return undefined;
  const fromUids = negotiationObj?.uids?.[caseIndex];
  if (fromUids) return fromUids;
  return negotiationObj?.cases?.[caseIndex]?.parcel_id;
}

function getStreamGuardResult(target) {
  const ref = target?.ref;
  const caseIndex = target?.caseIndex;
  if (!ref?.properties || typeof caseIndex !== 'number' || caseIndex < 0) {
    return { ok: false, reason: 'missing_ref_or_bad_caseIndex', caseIndex };
  }
  if (Array.isArray(ref.updated) && ref.updated[caseIndex] !== true) {
    return { ok: false, reason: 'stream_not_updated_yet', caseIndex, updatedAtIndex: ref.updated?.[caseIndex] };
  }
  const regular = ref.properties?.[0]?.[caseIndex];
  const pidArr = regular?.parcel_id ?? regular?.ParcelID;
  const subjectPid = Array.isArray(pidArr) ? pidArr[0] : undefined;
  if (!regular) return { ok: false, reason: 'no_regular_row_yet', caseIndex };
  if (!Array.isArray(pidArr)) return { ok: false, reason: 'missing_subject_pid_array', caseIndex, subjectPid };
  if (!subjectPid) return { ok: false, reason: 'missing_subject_pid_value', caseIndex, subjectPid };
  const expectedSubject = expectedSubjectPidForCase(target?.negotiationObj, caseIndex);
  if (expectedSubject && subjectPid !== expectedSubject) {
    return {
      ok: false,
      reason: 'subject_pid_mismatch_stale_ref',
      caseIndex,
      subjectPid,
      expectedSubjectPid: expectedSubject,
    };
  }
  // Stream complete (updated=true) is enough; KNN may return only the subject.
  return { ok: true, reason: 'ok', caseIndex, subjectPid };
}

async function fetchBatch(items, taxYear, signal) {
  if (!items.length) return {};

  // Dedupe by lookup key within the batch (same txn can appear for multiple cases)
  const seen = new Set();
  const unique = [];
  items.forEach((i) => {
    if (!i.key || seen.has(i.key)) return;
    seen.add(i.key);
    unique.push(i);
  });

  const uncached = unique.filter(i => !shared.cache.has(i.key) && !shared.pending.has(i.key));

  const batchPromise = uncached.length
    ? axiosInstance
        .post('/lookup_property_inventory_batch', {
          lookups: uncached.map(({ key, parcel_id, transaction_id }) => ({
            key,
            parcel_id: parcel_id ?? null,
            transaction_id: transaction_id ?? null,
          })),
          year: taxYear,
        }, { signal })
        .then(res => {
          const batchResults = res.data || {};
          const out = {};
          uncached.forEach((item) => {
            const row = normalizeCompRow(batchResults[item.key]?.[0]) ?? null;
            // Only cache hits — empty misses must remain re-fetchable on retry
            if (row) shared.cache.set(item.key, row);
            out[item.key] = row;
          });
          return out;
        })
        .catch(err => {
          logger.error('[CompLookup] lookup_property_inventory_batch error', uncached, taxYear, err?.message);
          return {};
        })
    : Promise.resolve({});

  uncached.forEach(({ key }) => {
    if (!shared.pending.has(key)) {
      shared.pending.set(
        key,
        batchPromise.then(out => out[key] ?? null).finally(() => shared.pending.delete(key)),
      );
    }
  });

  const freshResults = await batchPromise;

  const result = {};
  unique.forEach(({ key }) => {
    result[key] = shared.cache.has(key) ? shared.cache.get(key) : (freshResults[key] ?? null);
  });
  return result;
}

function mergeIntoRef(row, { ref, caseIndex, negotiationObj, insertIndex = null, panes = [0, 1], reportType, transactionId = null }) {
  if (!row || !ref?.properties) return;

  const extractSubjectValues = target => {
    if (!target?.parcel_id?.length) return null;
    const fields = ['IFMV', 'Sqft', 'ConditionIn', 'Grade', 'Bath', 'HalfBath', 'TotalGarageCap', 'Acres'];
    return fields.reduce((acc, f) => {
      if (target[f]?.[0] !== undefined) acc[f] = target[f][0];
      return acc;
    }, {});
  };

  // When we looked up by transaction id, force that id onto the row (SP may omit/alter it)
  if (transactionId != null && transactionId !== '') {
    row = { ...row, TransactionID: transactionId };
  }

  const rt = reportType || negotiationObj?.reportType;
  const lookupKey = idKey(rt);
  const compId = lookupKey === 'TransactionID'
    ? (row.TransactionID ?? transactionId)
    : (row.parcel_id ?? row.ParcelID ?? '');

  (panes || [0, 1]).forEach((pane) => {
    const target = ref.properties?.[pane]?.[caseIndex];
    if (!target?.parcel_id) return;
    if (findCompIndex(target, compId, rt) >= 0) return;
    processCompRow(row, target, negotiationObj, extractSubjectValues(target), insertIndex);
  });
}

function drain() {
  if (shared.draining || shared.queue.length === 0) return;
  shared.draining = true;
  const maxConcurrent = shared.maxConcurrent;

  const runBatch = async () => {
    try {
      while (shared.queue.length > 0) {
        const batch = shared.queue.splice(0, BATCH_SIZE);
        batch.forEach(t => {
          const k = compLookupTaskKey(t);
          shared.queuedKeys.delete(k);
          shared.inFlightKeys.add(k);
        });
        const byYear = new Map();
        batch.forEach(t => {
          if (!byYear.has(t.taxYear)) byYear.set(t.taxYear, []);
          byYear.get(t.taxYear).push(t);
        });

        const yearGroups = [...byYear.entries()];
        for (let i = 0; i < yearGroups.length; i += maxConcurrent) {
          const slice = yearGroups.slice(i, i + maxConcurrent);
          await Promise.all(slice.map(async ([year, tasks]) => {
            const items = tasks.map(t => ({
              key: lookupCacheKey(t),
              parcel_id: t.pid ?? null,
              transaction_id: t.transactionId ?? null,
            }));
            let results;
            try {
              results = await fetchBatch(items, year, null);
            } catch {
              results = {};
            }

            tasks.forEach(task => {
              const k = compLookupTaskKey(task);
              const cacheKey = lookupCacheKey(task);
              const row = results[cacheKey] ?? null;
              if (row) {
                mergeIntoRef(row, {
                  ...task.target,
                  reportType: task.reportType,
                  transactionId: task.transactionId,
                });
                shared.inFlightKeys.delete(k);
              } else if ((task.retries ?? 0) < MAX_RETRIES) {
                shared.inFlightKeys.delete(k);
                shared.queuedKeys.add(k);
                shared.queue.push({ ...task, retries: (task.retries ?? 0) + 1 });
              } else {
                shared.inFlightKeys.delete(k);
                logger.warn(`[CompLookup] Dropping ${task.pid != null ? `pid=${task.pid}` : `transactionId=${task.transactionId}`} after ${MAX_RETRIES} retries`, {
                  year,
                  caseIndex: task.target?.caseIndex,
                  reportType: task.reportType,
                });
              }
            });
          }));
        }

        await new Promise(r => setTimeout(r, 0));
      }
    } finally {
      shared.draining = false;
      // Items may have been enqueued while draining=true (drain() no-op'd) — kick again
      if (shared.queue.length > 0) drain();
    }
  };

  setTimeout(() => runBatch().catch(e => {
    logger.error('[CompLookup] drain error:', e);
    shared.draining = false;
    if (shared.queue.length > 0) drain();
  }), 0);
}

function enqueueLookup({ pid = null, transactionId = null, taxYear, signal, target, reportType }) {
  const cacheKey = lookupCacheKey({ pid, transactionId });
  if (!cacheKey || !target?.ref || typeof target.caseIndex !== 'number') return;
  const guard = getStreamGuardResult(target);
  if (!guard.ok) return;

  if (shared.cache.has(cacheKey)) {
    const row = shared.cache.get(cacheKey);
    if (row) {
      setTimeout(() => mergeIntoRef(row, { ...target, reportType, transactionId }), 0);
    }
    return;
  }

  const task = { pid, transactionId, taxYear, signal, target, retries: 0, reportType };
  const key = compLookupTaskKey(task);
  if (shared.queuedKeys.has(key) || shared.inFlightKeys.has(key)) {
    return;
  }

  shared.queuedKeys.add(key);
  shared.queue.push(task);

  clearTimeout(shared.debounceTimer);
  if (shared.queue.length >= BATCH_SIZE) {
    drain();
  } else {
    shared.debounceTimer = setTimeout(drain, BATCH_DEBOUNCE_MS);
  }
}

function enqueueMissingSavedComps({
  savedCompIds = [], existingCompIds = [], taxYear, signal,
  ref, caseIndex, negotiationObj, insertIndex = null, panes = [0, 1],
  reportType,
}) {
  const rt = reportType || negotiationObj?.reportType || 'standard';
  const key = idKey(rt);
  const existing = new Set(
    existingCompIds.map(normalizeCompId).filter(Boolean),
  );
  const missing = savedCompIds.filter(
    (id) => id != null && id !== '' && !existing.has(normalizeCompId(id)),
  );

  missing.forEach((id) => {
    const byTxn = key !== 'parcel_id';
    enqueueLookup({
      pid: byTxn ? null : id,
      transactionId: byTxn ? id : null,
      taxYear,
      signal,
      reportType: rt,
      target: { ref, caseIndex, negotiationObj, insertIndex, panes },
    });
  });
  return missing.length;
}

function getStatus() {
  return { cacheSize: shared.cache.size, pending: shared.queue.length, running: 0 };
}

function hasActiveWork() {
  return shared.queue.length > 0 || shared.draining;
}

function clearQueue() {
  shared.queue.forEach(t => shared.queuedKeys.delete(compLookupTaskKey(t)));
  shared.queue.length = 0;
}

function clearCache() {
  shared.cache.clear();
}

function flush(onComplete, timeoutMs = 15_000) {
  drain();
  return new Promise(resolve => {
    const deadline = Date.now() + timeoutMs;
    const check = () => {
      if (!shared.draining && shared.queue.length === 0) {
        onComplete?.();
        resolve();
      } else if (Date.now() > deadline) {
        logger.warn('[CompLookup] flush() timed out — clearing queue');
        shared.queue.forEach(t => shared.queuedKeys.delete(compLookupTaskKey(t)));
        shared.queue.length = 0;
        shared.draining = false;
        onComplete?.();
        resolve();
      } else {
        setTimeout(check, 100);
      }
    };
    setTimeout(check, 100);
  });
}

function getQueueStats() {
  return {
    total: shared.queue.length,
    byYear: Array.from(
      shared.queue.reduce((acc, t) => {
        acc.set(t.taxYear, (acc.get(t.taxYear) || 0) + 1);
        return acc;
      }, new Map()).entries(),
    ),
    canBatch: shared.queue.length >= BATCH_SIZE,
    isDraining: shared.draining,
  };
}

/** Immediate drain kick (used by useStreamProcessorNew after stream). */
function forceBatchProcess() {
  clearTimeout(shared.debounceTimer);
  shared.debounceTimer = null;
  drain();
}

function processBatch() {
  forceBatchProcess();
}

function getBatchThreshold() {
  return BATCH_SIZE;
}

const sharedApi = {
  enqueueLookup,
  enqueueMissingSavedComps,
  getStatus,
  hasActiveWork,
  flush,
  clearQueue,
  clearCache,
  getQueueStats,
  forceBatchProcess,
  processBatch,
  getBatchThreshold,
  _cache: { get current() { return shared.cache; } },
};

const disabledApi = {
  enqueueLookup: () => {},
  enqueueMissingSavedComps: () => 0,
  getStatus: () => ({ cacheSize: 0, pending: 0, running: 0 }),
  hasActiveWork: () => false,
  flush: async () => {},
  clearQueue: () => {},
  clearCache: () => {},
  getQueueStats: () => ({}),
  forceBatchProcess: () => {},
  processBatch: () => {},
  getBatchThreshold: () => BATCH_SIZE,
  _cache: { current: new Map() },
};

/** Module-level flush for post-stream callers (e.g. autosave NAL check) that cannot use the hook. */
export const flushCompLookups = (onComplete, timeoutMs) =>
  (DISABLE_COMP_LOOKUPS ? disabledApi : sharedApi).flush(onComplete, timeoutMs);

export const hasActiveCompLookups = () =>
  (DISABLE_COMP_LOOKUPS ? disabledApi : sharedApi).hasActiveWork();

/**
 * Non-blocking comp lookup system (shared singleton — see `shared` above).
 */
export function useCompLookups({ maxConcurrent = 8 } = {}) {
  useEffect(() => {
    shared.maxConcurrent = Math.max(shared.maxConcurrent, maxConcurrent);
  }, [maxConcurrent]);

  const cacheVersion = usePersistedDataStore(s => s.compLookupCacheInvalidationVersion);
  const versionRef = useRef(cacheVersion);
  useEffect(() => {
    if (cacheVersion !== versionRef.current && cacheVersion > 0) {
      versionRef.current = cacheVersion;
      shared.cache.clear();
    }
  }, [cacheVersion]);

  return useMemo(
    () => (DISABLE_COMP_LOOKUPS ? disabledApi : sharedApi),
    [],
  );
}

function normalizeCompRow(raw) {
  if (!raw) return raw;
  const parcel_id = raw.parcel_id ?? raw.ParcelID ?? raw.PID ?? '';
  const Latitude = raw.Latitude ?? raw.latitude ?? null;
  const Longitude = raw.Longitude ?? raw.longitude ?? null;
  const Bath = raw.Bath ?? raw.BathsFull ?? raw.fullBaths ?? '';
  const HalfBath = raw.HalfBath ?? raw.BathsHalf ?? raw.halfBaths ?? '';
  const TotalGarageCapacity = raw.TotalGarageCapacity ?? raw.GarageCapacity ?? '';
  const TransactionID = raw.TransactionID ?? raw.trans_id ?? null;
  return { ...raw, parcel_id, ParcelID: parcel_id, TransactionID, Latitude, Longitude, Bath, HalfBath, TotalGarageCapacity };
}
